<template>
  <div class="hosts-dashboard-content">
    <el-row :type="'flex'">
      <el-col :span="24">
        <el-row :type="'flex'">
          <el-col :span="3">
            <div class="scroll-menu full-height">
              <h5>Insights</h5>
              <el-link :underline="false" @click="scrollToElementID('insights', 'workload-profile-section')"
                >Workload Profile</el-link
              >
              <el-link :underline="false" @click="scrollToElementID('insights', 'notables-section')">Notables</el-link>
            </div>
          </el-col>
          <el-col :span="21">
            <el-row id="workload-profile-section" class="workload-profiler_header el-collapse-item__header"
              >Workload Profile</el-row
            >
            <el-row :type="'flex'" class="insight-graphs">
              <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
                <sunburst-chart :agentId="agentId"></sunburst-chart>
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
                <horizon-chart
                  :ops="['Network', 'Executes', 'Files', 'Users']"
                  :agentId="agentId"
                  v-if="isWorkloadProfileLoaded"
                ></horizon-chart>
              </el-col>
            </el-row>
            <el-divider></el-divider>
            <el-row id="notables-section" :type="'flex'" class="most-active-insights" :gutter="20">
              <el-col :xs="24" :sm="8" :md="8" :lg="8" :xl="8">
                <most-active-card
                  :title="'Most Active Programs'"
                  :name="'programs'"
                  :agent-id="agentId"
                  :col-headers="['Program', 'Count']"
                  @row-click="gotoThreatHunting"
                  ref="mostactiveprogram"
                ></most-active-card>
              </el-col>
              <el-col :xs="24" :sm="8" :md="8" :lg="8" :xl="8">
                <most-active-card
                  v-if="isAgentDetailsLoaded"
                  :title="'Most Active Users'"
                  :name="'users'"
                  :agent-id="agentId"
                  :os-type="agentOSType"
                  :col-headers="['User', 'Count']"
                  @row-click="gotoThreatHunting"
                  ref="mostactiveprogram"
                ></most-active-card>
              </el-col>
              <el-col :xs="24" :sm="8" :md="8" :lg="8" :xl="8">
                <most-active-card
                  :title="'Most Active Ports'"
                  :name="'ports'"
                  :agent-id="agentId"
                  :col-headers="['Port', 'Program', 'Count']"
                  @row-click="gotoThreatHunting"
                  ref="mostactiveprogram"
                ></most-active-card>
              </el-col>
            </el-row>
            <el-divider></el-divider>
            <el-row>
              <el-col class="full-width display-block float-right mb-10">
                <date-range-dropdown
                  class="float-right"
                  :date-range-options="dateRangeOptions"
                  :store-pref-constant="daterangeUserPrefConstant"
                  :date-range-val="hostInsightsDateRangeUserSetting"
                ></date-range-dropdown>
              </el-col>
            </el-row>
            <div v-if="isAgentDetailsLoaded">
              <windows-host-insights :agent-id="agentId" v-if="agentOSType == 'windows'"></windows-host-insights>
              <linux-host-insights :agent-id="agentId" v-else></linux-host-insights>
            </div>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment-timezone'
import PageHeaders from '@/components/layout/PageHeaders.vue'
import InsightCard from '@/views/hosts/InsightCard.vue'
import MostActiveCard from '@/views/hosts/MostActiveCard.vue'
import SunburstChart from '@/components/chart/SunburstChart.vue'
import HorizonChart from '@/components/chart/HorizonChart.vue'
import WindowsHostInsights from '@/views/hosts/WindowsHostInsights.vue'
import LinuxHostInsights from '@/views/hosts/LinuxHostInsights.vue'

import {
  DATETIME_FORMAT_LONG,
  NANOSECONDS_TO_MILLISECONDS,
  INSIGHTS_DATE_RANGE_CHOICES
} from '@/constants/datetime.constants'

import DateRangeDropdown from '@/components/dropdown/DateRangeDropdown.vue'

import { AGENT_DETAILS_REQ } from '@/store/modules/agents/agents.type'
import { HOST_INSIGHTS_DATE_RANGE_PREFERENCE } from '@/store/modules/user_pref/user-pref.type'

export default {
  name: 'HostDashboard',
  data () {
    return {
      daterangeUserPrefConstant: HOST_INSIGHTS_DATE_RANGE_PREFERENCE,
      dateRangeOptions: INSIGHTS_DATE_RANGE_CHOICES,
      agendId: '',
      customHostname: '',
      breadcrumbs: [{ title: 'Dashboard' }, { title: 'Hosts', to: '/assets/hosts' }],
      agent: null,
      warning: false,
      activeDetectionsCollapse: [],
      activeDetailsCollapse: ['host-details', 'host-agent', 'host-network', 'host-configuration'],
      activeInsightsCollapse: [],
      activeContainersCollapse: [],
      paneHeightStyle: {
        height: '500px'
      }
    }
  },
  created () {
    if (!this.$parent.$parent.collapseNav) {
      this.$parent.$parent.collapseNav = true
    }
    this.resize()
    this.agentId = this.$route.params.agentId

    // always load tab details
    this.loadTabDetails()

    // add resize listener to window
    window.addEventListener('resize', (this.resizeListener = this.resize.bind(this)))
  },
  beforeDestroy () {
    // remove resize listener from window
    window.removeEventListener('resize', this.resizeListener)
  },
  methods: {
    resize () {
      let OFFSET_TOP_DISTANCE = 172
      let tabPaneHeight = window.innerHeight - OFFSET_TOP_DISTANCE

      this.paneHeightStyle.height = tabPaneHeight + 'px'
    },
    switchView (tab, event) {
      this.$router.replace({
        query: {
          view: tab.paneName
        }
      })
    },
    loadTabDetails () {
      this.$store.dispatch(AGENT_DETAILS_REQ, this.agentId).then(
        agent => {
          this.agent = agent
          this.agent.publicIPs = this.agent.publicIPs.sort()
          this.agent.privateIPs = this.agent.privateIPs.sort()
          this.customHostname = this.agent.customHostname
          if (this.breadcrumbs.length === 2) {
            this.breadcrumbs.push({ title: this.customHostname })
          }
          if (this.agent.aggregatedRiskScore > 0) {
            this.warning = 'at risk'
          }
          if (this.agent.containers.length > 0) {
            this.agent.containers.forEach(container => {
              this.activeContainersCollapse.push(container.containerId)
            })
          }
        },
        errMsg => {
          const h = this.$createElement
          this.$msgbox({
            title: 'Details Error',
            message: h('p', null, 'There was an error loading the agent you requested.'),
            showCancelButton: false,
            confirmButtonText: 'Ok',
            beforeClose: (action, instance, done) => {
              window.location.href = '/#/hosts'
            }
          })
        }
      )
    },
    insightsUpdated (update) {
      let name = update.name
      let actives = this.activeInsightsCollapse
      if (update.hasChartData) {
        actives.push(name)
      }
    },
    formatDateTime (datetime) {
      return moment(datetime).format(DATETIME_FORMAT_LONG)
    },
    formatDateTimeNS (datetime) {
      return this.formatDateTime(datetime * NANOSECONDS_TO_MILLISECONDS)
    },
    scrollToElementID (pane, id) {
      let OFFSET_TOP_DISTANCE = 169

      let val
      let tabs = this.$el
      let element = document.getElementById(id)
      let rect = element.getBoundingClientRect()
      let to = rect.y - OFFSET_TOP_DISTANCE + tabs.scrollTop

      let start = tabs.scrollTop
      let change = to - start
      let currentTime = 0
      let increment = 20
      let duration = 250
      let easeInOutQuad = (t, b, c, d) => {
        t /= d / 2
        if (t < 1) return (c / 2) * t * t + b
        t--
        return (-c / 2) * (t * (t - 2) - 1) + b
      }
      let animateScroll = () => {
        currentTime += increment
        val = easeInOutQuad(currentTime, start, change, duration)
        tabs.scrollTop = val
        if (currentTime < duration) {
          setTimeout(animateScroll, increment)
        }
      }
      animateScroll()
    },
    gotoThreatHunting (type) {
      if (!this.insightsEnabled) {
        return false
      }

      let threatHuntParam = `date_range:${this.hostInsightsDateRangeUserSetting}`
      switch (type.name) {
        case 'programs':
          threatHuntParam = `date_range:PAST_24_HOURS+edgeName:in:clone:ProcessStart+sourceComm:eq:${type.row['sourceComm']}+agentId:eq:${this.agentId}`
          break
        case 'users':
          if (this.agentOSType === 'windows') {
            threatHuntParam = `date_range:PAST_24_HOURS+edgeName:in:EXECUTES:DELETES:CREATES:MODIFIES+sourceWinSubjectUser:eq:${type.row['sourceWinSubjectUser']}+agentId:eq:${this.agentId}`
          } else {
            threatHuntParam = `date_range:PAST_24_HOURS+edgeName:in:EXECUTES:DELETES:CREATES:MODIFIES+sourceLinuxUsername:eq:${type.row['sourceLinuxUsername']}+agentId:eq:${this.agentId}`
          }
          break
        case 'ports':
          threatHuntParam = `date_range:PAST_24_HOURS+edgeName:in:network+sourceComm:eq:${type.row['sourceComm']}+sourcePort:eq:${type.row['sourcePort']}+agentId:eq:${this.agentId}`
          break
        default:
          threatHuntParam = `${threatHuntParam}+edgeName:in:clone:ProcessStart+sourceComm:eq:${type.row['sourceComm']}+agentId:eq:${this.agentId}`
          break
      }
      this.$router.push({
        name: 'threat-hunting',
        query: { search: threatHuntParam }
      })
    }
  },
  computed: {
    ...mapGetters([
      'insightsEnabled',
      'agentDetailsReqStatus',
      'hostInsightsDateRangeUserSetting',
      'hrlyDnsRequestStatisticsReqStatus',
      'hrlyExternalIpStatisticsReqStatus',
      'hrlyLoginFailuresStatisticsReqStatus',
      'hrlyPrivilegedExecStatisticsReqStatus',
      'hostEventCompositionReqStatus'
    ]),
    activeTab: {
      get: function () {
        if (this.insightsEnabled) {
          return this.$route.query.view || 'insights'
        } else {
          return this.$route.query.view || 'details'
        }
      },
      set: function (val) {}
    },
    isWorkloadProfileLoaded () {
      return this.hostEventCompositionReqStatus === 'saved'
    },
    isAgentDetailsLoaded () {
      return this.agent
    },
    agentOSType () {
      if (this.agent) {
        return this.agent.system.os.toLowerCase()
      }
      return 'windows'
    }
  },
  watch: {
    agentDetailsReqStatus: {
      handler: (newStatus, oldStatus) => {
        if (newStatus === 'error') {
          console.log('error')
        }
      }
    }
  },
  components: {
    PageHeaders,
    InsightCard,
    DateRangeDropdown,
    SunburstChart,
    HorizonChart,
    MostActiveCard,
    WindowsHostInsights,
    LinuxHostInsights
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';

.hosts-dashboard-content {
  overflow: scroll;
  height: calc(100% - 145px);
}

.insight-graphs {
  height: 350px;
  margin: 0px 0px 15px 0px;
}

.workload-profiler_header {
  height: 36px;
  padding-left: 15px;
  margin-bottom: 20px;
  font-weight: 700;
  background: var(--gray-100);
}

.scroll-menu {
  position: fixed;
}
.scroll-menu h5 {
  margin-top: 0.5em;
  margin-bottom: 1.5em;
}
.scroll-menu a {
  display: block;
  margin: 0.5em 0;
}

.most-active-insights,
.basic-insights {
  min-height: 350px;
  padding-bottom: 20px;
}

.top-ten_list {
  min-height: 255px;
}

.empty_text {
  min-height: 280px;
}

.topten-entities {
  height: 100% !important;
}

>>> .el-collapse-item__header {
  height: 36px;
  padding-left: 15px;
  font-weight: 700;
  background: #f4f4f4;
}

>>> .el-collapse-item__content {
  padding: 15px;

  .name {
    color: #999999;
    font-size: 12px;
    font-weight: bold;
  }

  .el-row {
    margin-bottom: 5px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/hosts/HostInsights.vue